import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  X,
  DownloadSimple,
  ChartLineUp,
  Calendar,
  MapPin,
  Leaf,
  Thermometer,
  CircleNotch,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DownloadModal({ startDate, endDate, lat, lon, activeLayer, onClose }) {
  const [dataType, setDataType] = useState(
    activeLayer === "lst" ? "temperature" : "ndvi"
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/timelapse/report`, {
          params: { start_date: startDate, end_date: endDate, lat, lon, data_type: dataType },
        });
        setReport(res.data);
      } catch {
        setReport(null);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [startDate, endDate, lat, lon, dataType]);

  const chartData = useMemo(() => {
    if (!report?.daily_data) return [];
    const field = dataType === "ndvi" ? "ndvi" : "temperature_c";
    const combined = [
      ...report.daily_data.map((d) => ({
        date: d.date,
        value: d[field],
        type: d[`${dataType === "ndvi" ? "ndvi" : "temp"}_source`],
      })),
      ...report.predictions.map((p) => ({
        date: p.date,
        prediction: p.predicted_value,
        type: "prediction",
      })),
    ];
    return combined;
  }, [report, dataType]);

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const url = `${API}/timelapse/download?start_date=${startDate}&end_date=${endDate}&lat=${lat}&lon=${lon}&data_type=${dataType}`;
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `conexus_${dataType}_${startDate}_to_${endDate}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      alert("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadJSON = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `conexus_${dataType}_${startDate}_to_${endDate}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const url = `${API}/export/pdf?start_date=${startDate}&end_date=${endDate}&lat=${lat}&lon=${lon}&data_type=${dataType}`;
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `conexus_${dataType}_${startDate}_to_${endDate}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      alert("PDF download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const s = report?.summary;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="download-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        className="bg-stone-900 rounded-2xl border border-stone-700 overflow-hidden max-w-3xl w-full shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="download-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 shrink-0">
          <div className="flex items-center gap-2">
            <ChartLineUp size={20} weight="bold" className="text-emerald-400" />
            <span className="text-base font-heading font-semibold text-white">
              Download Earth Data Report
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-white p-1.5 rounded-lg hover:bg-stone-800 transition-colors"
            data-testid="download-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Config row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5 font-body">
                Data Type
              </label>
              <div className="flex gap-2">
                {[
                  { id: "ndvi", label: "NDVI", Icon: Leaf, active: "bg-emerald-600/20 text-emerald-400 border border-emerald-600/40" },
                  { id: "temperature", label: "Temperature", Icon: Thermometer, active: "bg-orange-600/20 text-orange-400 border border-orange-600/40" },
                  { id: "all", label: "All", Icon: ChartLineUp, active: "bg-sky-600/20 text-sky-400 border border-sky-600/40" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setDataType(opt.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-body transition-colors ${
                      dataType === opt.id
                        ? opt.active
                        : "bg-stone-800 text-stone-400 border border-stone-700 hover:border-stone-600"
                    }`}
                    data-testid={`download-type-${opt.id}`}
                  >
                    <opt.Icon size={14} weight="bold" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5 font-body">
                Date Range
              </label>
              <div className="flex items-center gap-1.5 text-xs text-stone-300 bg-stone-800 rounded-lg px-3 py-2 border border-stone-700">
                <Calendar size={14} className="text-stone-500" />
                <span>{startDate}</span>
                <span className="text-stone-600">→</span>
                <span>{endDate}</span>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5 font-body">
                Location
              </label>
              <div className="flex items-center gap-1.5 text-xs text-stone-300 bg-stone-800 rounded-lg px-3 py-2 border border-stone-700">
                <MapPin size={14} className="text-stone-500" />
                <span>
                  {lat.toFixed(4)}, {lon.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

          {/* Summary stats */}
          {loading ? (
            <div className="py-12 text-center">
              <CircleNotch size={24} className="text-emerald-400 animate-spin mx-auto" />
              <p className="text-xs text-stone-500 mt-3 font-body">Generating report...</p>
            </div>
          ) : s ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Total Days", value: s.total_days },
                  { label: "Observed", value: s.observed_days },
                  { label: "Interpolated", value: s.interpolated_days },
                  { label: "Predictions", value: `+${s.prediction_days}` },
                  { label: "Average", value: dataType === "temperature" ? `${s.average}°C` : s.average },
                  { label: "Minimum", value: dataType === "temperature" ? `${s.minimum}°C` : s.minimum },
                  { label: "Maximum", value: dataType === "temperature" ? `${s.maximum}°C` : s.maximum },
                  { label: "Std Dev", value: s.std_deviation },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50"
                  >
                    <p className="text-[10px] text-stone-500 uppercase tracking-wider font-body">
                      {stat.label}
                    </p>
                    <p className="text-sm font-heading font-semibold text-white mt-0.5">
                      {stat.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div>
                <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-3 font-body">
                  {dataType === "temperature" ? "Temperature" : "NDVI"} Trend & Predictions
                </h3>
                <div className="bg-stone-800/40 rounded-xl p-3 border border-stone-700/50">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="valGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="predGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#78716c", fontSize: 9 }}
                        axisLine={{ stroke: "#44403c" }}
                        tickLine={false}
                        interval="preserveStartEnd"
                        tickFormatter={(v) => v?.slice(5) || ""}
                      />
                      <YAxis
                        tick={{ fill: "#78716c", fontSize: 10 }}
                        axisLine={{ stroke: "#44403c" }}
                        tickLine={false}
                        domain={["auto", "auto"]}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#1c1917",
                          border: "1px solid #44403c",
                          borderRadius: "8px",
                          fontSize: "11px",
                          color: "#fff",
                        }}
                        formatter={(val, name) => [
                          val != null ? Number(val).toFixed(4) : "—",
                          name === "value" ? "Estimated" : "Predicted",
                        ]}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <ReferenceLine y={s.average} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.5} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#10b981"
                        fill="url(#valGrad)"
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                      />
                      <Area
                        type="monotone"
                        dataKey="prediction"
                        stroke="#f59e0b"
                        fill="url(#predGrad)"
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                        dot={false}
                        connectNulls
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 px-2">
                    <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                      <span className="w-3 h-0.5 bg-emerald-500 rounded" /> Estimated
                    </span>
                    <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                      <span className="w-3 h-0.5 bg-amber-500 rounded border-dashed" /> Predicted
                    </span>
                    <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
                      <span className="w-3 h-0.5 bg-emerald-500/50 rounded border-dashed" /> Average
                    </span>
                  </div>
                </div>
              </div>

              {/* What's included */}
              <div className="bg-stone-800/30 rounded-xl p-4 border border-stone-700/30">
                <h4 className="text-xs font-heading font-medium text-stone-300 mb-2">
                  What&apos;s in this report?
                </h4>
                <ul className="space-y-1.5 text-[11px] text-stone-500 font-body">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-stone-400">Daily estimates</strong> — {s.total_days} days of{" "}
                      {dataType === "all" ? "NDVI & temperature" : dataType} data with source classification
                      (observed vs. interpolated)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-stone-400">7-day predictions</strong> — Forward projections
                      based on trend analysis with confidence scores
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-stone-400">Summary statistics</strong> — Average, min, max,
                      standard deviation, and daily trend rate
                    </span>
                  </li>
                </ul>
              </div>

              {/* Data explanations & color system */}
              <div className="bg-stone-800/30 rounded-xl p-4 border border-stone-700/30">
                <h4 className="text-xs font-heading font-medium text-stone-300 mb-2">Understanding Your Data</h4>
                <div className="space-y-2 text-[11px] text-stone-500 font-body">
                  {dataType !== "temperature" && (
                    <div>
                      <p className="text-stone-400 font-medium mb-1">NDVI (Normalized Difference Vegetation Index)</p>
                      <p>Ranges from -1.0 to +1.0. Water/bare soil shows values below 0.1. Values above 0.3 indicate healthy vegetation. Dense forest typically shows 0.6-0.9.</p>
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        {[
                          { color: "#6b3a2a", label: "Bare (<0.1)" }, { color: "#c4a265", label: "Sparse (0.1-0.25)" },
                          { color: "#FFD700", label: "Low (0.25-0.4)" }, { color: "#90EE90", label: "Moderate (0.4-0.55)" },
                          { color: "#228B22", label: "Dense (0.55-0.7)" }, { color: "#006400", label: "Very Dense (>0.7)" },
                        ].map((c) => (
                          <span key={c.label} className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                            <span className="text-[10px]">{c.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {dataType !== "ndvi" && (
                    <div>
                      <p className="text-stone-400 font-medium mb-1">Temperature</p>
                      <p>Estimated surface temperature in °C based on latitude, season, and elevation models.</p>
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        {[
                          { color: "#1e40af", label: "Freezing" }, { color: "#3b82f6", label: "Cold" },
                          { color: "#06b6d4", label: "Cool" }, { color: "#10b981", label: "Mild" },
                          { color: "#f59e0b", label: "Warm" }, { color: "#ef4444", label: "Hot" },
                        ].map((c) => (
                          <span key={c.label} className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                            <span className="text-[10px]">{c.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-stone-400 font-medium mb-1">Data Sources</p>
                    <p><strong>Observed:</strong> Aligned with MODIS satellite 16-day composite periods (highest confidence: 0.95).</p>
                    <p><strong>Interpolated:</strong> Statistically estimated for days between observations (confidence: ~0.75).</p>
                    <p><strong>Predicted:</strong> 7-day forward projection based on trend analysis (confidence decreases over time).</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-stone-500 text-sm">
              Failed to generate report. Try adjusting the date range.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-800 flex items-center justify-between shrink-0">
          <p className="text-[10px] text-stone-600 font-body">
            Data based on MODIS satellite observations and statistical modeling
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadJSON}
              disabled={!report || loading}
              className="flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-stone-700"
              data-testid="download-json-btn"
            >
              <DownloadSimple size={14} />
              JSON
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={!report || loading || downloading}
              className="flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-stone-700"
              data-testid="download-pdf-btn"
            >
              <DownloadSimple size={14} />
              PDF
            </button>
            <button
              onClick={handleDownloadCSV}
              disabled={!report || loading || downloading}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="download-csv-btn"
            >
              {downloading ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <DownloadSimple size={14} weight="bold" />
              )}
              CSV
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
